import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import apiConfig from './pages/credentials/apiConfig';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import SubscriptionModal from './pages/credentials/SubscriptionModal';
import STRIPE_PUBLIC_KEY from './stripeConfig';
import { jwtDecode } from 'jwt-decode';

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

// Create Auth Context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(
    !!sessionStorage.getItem("authToken")
  );
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [userData, setUserData] = useState(null);

  // Check if user is logged in and has subscription
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (isUserLoggedIn) {
        setIsLoading(true);
        try {
          const userId = localStorage.getItem("userId");
          const authToken = localStorage.getItem("authToken");

          if (!userId || !authToken) {
            setHasSubscription(false);
            setIsLoading(false);
            return;
          }

          const response = await axios.get(
            `${apiConfig.apiBaseURL}/getUserProfile/${userId}`,
            {
              headers: {
                "x-api-key": apiConfig["x-api-key"],
                "Content-Type": "application/json",
                "Authorization": `Bearer ${authToken}`
              }
            }
          );

          if (response.data && response.data.success && response.data.user) {
            setUserData(response.data.user);
            // Check if user has stripeSubscriptionId
            setHasSubscription(!!response.data.user.stripeSubscriptionId);
          } else {
            setHasSubscription(false);
          }
        } catch (error) {
          console.error("Error checking authentication status:", error);
          setHasSubscription(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [isUserLoggedIn]);

  // Handle subscription modal visibility
  useEffect(() => {
    if (isUserLoggedIn && !isLoading && !hasSubscription) {
      setShowSubscriptionModal(true);
    } else {
      setShowSubscriptionModal(false);
    }
  }, [isUserLoggedIn, isLoading, hasSubscription]);

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setIsUserLoggedIn(!!sessionStorage.getItem("authToken"));
    };

    window.addEventListener("storage", handleStorageChange);

    // Listen for custom auth-updated event
    const handleAuthUpdate = () => {
      setIsUserLoggedIn(!!sessionStorage.getItem("authToken"));
    };
    window.addEventListener("auth-updated", handleAuthUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth-updated", handleAuthUpdate);
    };
  }, []);
  // Check for existing token on mount and set up cross-tab communication
  useEffect(() => {
    // Check if we have a token in sessionStorage (survives refreshes but not browser close)
    const token = sessionStorage.getItem('authToken');

    if (token) {
      // We're already logged in
      setIsUserLoggedIn(true);

      // Signal to other tabs that we have an active session
      // This uses temporary storage that's only for tab communication
      localStorage.setItem('tabSessionActive', Date.now().toString());
      setTimeout(() => localStorage.removeItem('tabSessionActive'), 500);
    } else {
      // We don't have a token - check if we should request it from another tab
      // This happens when opening a new tab via right-click
      localStorage.setItem('requestAuthToken', Date.now().toString());
      setTimeout(() => localStorage.removeItem('requestAuthToken'), 500);
    }

    // Handle storage events for cross-tab communication
    const handleStorageEvent = (e) => {
      // Another tab is requesting our token
      if (e.key === 'requestAuthToken') {
        const ourToken = sessionStorage.getItem('authToken');
        if (ourToken) {
          // Share our token (temporarily)
          localStorage.setItem('shareAuthToken', ourToken);
          setTimeout(() => localStorage.removeItem('shareAuthToken'), 500);
        }
      }

      // Another tab shared its token with us
      if (e.key === 'shareAuthToken' && e.newValue) {
        // Only use it if we don't already have a token
        if (!sessionStorage.getItem('authToken') && !!localStorage.getItem('authToken')) {
          sessionStorage.setItem('authToken', e.newValue);
          setIsUserLoggedIn(true);
        }
      }

      // Another tab logged out
      if (e.key === 'logoutEvent') {
        sessionStorage.removeItem('authToken');
        localStorage.removeItem('authToken');
        localStorage.removeItem('isLoggedIn');
        setIsUserLoggedIn(false);
      }
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, []);
  // Login function
  const login = (token, userId) => {
    localStorage.setItem("authToken", token);
    sessionStorage.setItem("authToken", token);
    localStorage.setItem('isLoggedIn', 'true');
    if (userId) localStorage.setItem("userId", userId);
    setIsUserLoggedIn(true);
    // Dispatch an event to notify components about auth change
    window.dispatchEvent(new Event('auth-updated'));
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("authToken");
    localStorage.removeItem("loginResponseId");
    setIsUserLoggedIn(false);
    setHasSubscription(false); // Reset subscription state too
    setShowSubscriptionModal(false); // Force modal to close when logging out
    // Notify other tabs about logout
    localStorage.setItem('logoutEvent', Date.now().toString());
    // Clean up after a short delay
    setTimeout(() => {
      localStorage.removeItem('logoutEvent');
    }, 500);
    // Dispatch an event to notify components about auth change
    window.dispatchEvent(new Event('auth-updated'));
  };

  // Handle subscription success
  const handleSubscriptionSuccess = () => {
    setHasSubscription(true);
    setShowSubscriptionModal(false);
  };

  useEffect(() => {
    const interceptor = axios.interceptors.request.use(async (config) => {
      const token = localStorage.getItem("authToken");

      // 🚫 Skip refreshing token for the refreshToken endpoint
      if (config.url?.includes("/refreshToken")) {
        return config;
      }

      if (token) {
        try {
          const decoded = jwtDecode(token);
          const now = Date.now() / 1000;
          // ⏱️ Refresh the token if it expires in less than 2 minutes
          if (decoded.exp && decoded.exp - now < 120) {
            const response = await axios.post(
              `${apiConfig.apiBaseURL}/refreshToken`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "x-api-key": apiConfig["x-api-key"],
                  "Content-Type": "application/json",
                },
              }
            );

            const newToken = response.data?.token;
            if (newToken) {
              localStorage.setItem("authToken", newToken);
              sessionStorage.setItem("authToken", newToken);
              config.headers["Authorization"] = `Bearer ${newToken}`;
            }
          } else {
            config.headers["Authorization"] = `Bearer ${token}`;
          }
        } catch (error) {
          console.error("Error refreshing token:", error);
          logout(); // Or redirect to login
        }
      }

      return config;
    }, (error) => Promise.reject(error));

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const status = error.response?.status;
        if (status === 401) {
          logout();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Context value
  const value = {
    isUserLoggedIn,
    hasSubscription,
    isLoading,
    userData,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Subscription Modal */}
      {/* <Elements stripe={stripePromise}> */}
      {/* <SubscriptionModal
          show={showSubscriptionModal}
          handleClose={() => {
            // If user closes without subscribing, log them out
            if (!hasSubscription) {
              logout();
            }
          }}
          onSubscriptionSuccess={handleSubscriptionSuccess}
        /> */}
      {/* </Elements> */}

      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};